import { ComponentChildren, h, Ref } from "preact";
import { useCallback, useLayoutEffect, useRef } from "preact/hooks";
import "./dialog.scss";

// todo: consider maintaining a global stack of open dialogs, in order to control their backdrops and avoid stacking backdrops resulting in cumulative darkening.
// ideally the top backdrop and the backdrop from the dialog just below would fade at the same time, resulting in a smooth transition.

// todo: add so the "animate" class also work with "centered", to animates the modal down like 16 pixels or so, similar to how alert() does
// todo: add a class that switches with from auto to fit-content

// https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/dialog

interface Props {
    id?: string;
    open?: boolean;
    modal?: boolean;
    closeOnBackdrop?: boolean;
    className?: string;
    children: ComponentChildren;
    onClose?: () => void;
}

export function Dialog(props: Props) {
    const myRef = useRef<HTMLDialogElement>(null);

    const onClick = useCallback((e: h.JSX.TargetedMouseEvent<HTMLDialogElement>) => {
        if (props.closeOnBackdrop) {
            myRef.current?.close();
            //props.onClose?.();
        }
    }, []);

    const onClose = useCallback((e: h.JSX.TargetedEvent<HTMLDialogElement, Event>) => {
        props.onClose?.();
    }, []);

    useLayoutEffect(() => {
        function handleTransitionEnd(e: TransitionEvent) {
            if (myRef.current?.classList.contains("closing") && e.propertyName === "transform") {
                myRef.current?.classList.remove("closing");
                myRef.current?.close();
            }
        }

        function handleCancel(e: Event) {
            e.preventDefault();
            if (myRef.current?.classList.contains("animate")) {
                myRef.current?.classList.add("closing");
            } else {
                myRef.current?.close();
                myRef.current?.removeAttribute("open");
            }
        }

        function handleClick(e: MouseEvent) {
            if (!props.closeOnBackdrop || !myRef.current) return;
            const bounds = myRef.current.getBoundingClientRect();
            if (e.clientX < bounds.left ||
                e.clientX > bounds.right ||
                e.clientY < bounds.top ||
                e.clientY > bounds.bottom) {
                handleCancel(e);
            }
        }

        myRef.current?.addEventListener("transitionend", handleTransitionEnd);
        myRef.current?.addEventListener("cancel", handleCancel);
        myRef.current?.addEventListener("mousedown", handleClick);
        return () => {
            myRef.current?.removeEventListener("transitionend", handleTransitionEnd);
            myRef.current?.removeEventListener("cancel", handleCancel);
            myRef.current?.removeEventListener("mousedown", handleClick);
        };
    }, [props.closeOnBackdrop]);

    useLayoutEffect(() => {
        if (props.open && myRef.current && !myRef.current.hasAttribute("open")) {
            if (props.modal !== false) {
                myRef.current.showModal();
            } else {
                myRef.current.show();
            }
        }
        if (!props.open && myRef.current && myRef.current.hasAttribute("open")) {
            if (myRef.current.classList.contains("animate")) {
                myRef.current.classList.add("closing");
            } else {
                myRef.current.close();
            }
        }
    }, [props.open]);

    return (
        <dialog id={props.id} ref={myRef} class={"component-dialog " + (props.className ?? "centered")} onClose={onClose}>
            {
                props.children
            }
        </dialog>
    );
}
